import { BreakpointObserver } from '@angular/cdk/layout';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { AppAuthService } from '@src/app/core/services/app-auth.service';
import { SIGN_IN_STEP } from '@src/app/mepco-public/models/mfa-challenges.model';
import { map } from 'rxjs/operators';

import { MOBILE_BREAKPOINT } from '../mfa.constants';

@Component({
  selector: 'app-mfa-dialog',
  standalone: false,
  templateUrl: './mfa-dialog.component.html',
  styleUrl: './mfa-dialog.component.scss',
})
export class MFAComponent {
  dialogRef = inject(MatDialogRef<MFAComponent>);
  readonly data: { gotToVerificationStep: boolean; challengeName: SIGN_IN_STEP; deliveryDestination: string } =
    inject(MAT_DIALOG_DATA);
  stepper = viewChild<MatStepper>('stepper');
  selectedMethod = new FormControl<SIGN_IN_STEP>(null, { validators: [Validators.required] });
  mustVerify = signal<boolean>(false);

  private _authService = inject(AppAuthService);
  private _breakpointObserver = inject(BreakpointObserver);

  private _isMobile = toSignal(this._breakpointObserver.observe(MOBILE_BREAKPOINT).pipe(map(state => state.matches)), {
    initialValue: this._breakpointObserver.isMatched(MOBILE_BREAKPOINT),
  });

  stepperOrientation = computed<'horizontal' | 'vertical'>(() => (this._isMobile() ? 'vertical' : 'horizontal'));

  showPhoneOption = signal<boolean>(false);
  showEmailOption = signal<boolean>(false);

  title = 'Choose your verification method';
  description = `Select how you want to receive your security codes.<br>
                 We support multiple methods to help keep your account secure.`;

  selectType = SIGN_IN_STEP.SELECT_MFA_TYPE;
  appType = SIGN_IN_STEP.SOFTWARE_TOKEN_MFA;
  smsType = SIGN_IN_STEP.SMS_MFA;
  emailType = SIGN_IN_STEP.EMAIL_MFA;

  STEP_2_CONTENT = {
    [SIGN_IN_STEP.SOFTWARE_TOKEN_MFA]: {
      title: 'Configure Authenticator App',
      description: `Open your authenticator app and scan the QR code below or enter the code manually to link your account . <br>
                    To keep your account secure, you must set up an authenticator app (Google Authenticator, Microsoft Authenticator, Authy, etc.).<br><br>
                    Add a new account to your authenticator app using one of the methods below.`,
    },
    [SIGN_IN_STEP.SMS_MFA]: {
      title: 'Verify your phone number',
      description: 'We will send a 6-digit verification code to your phone via SMS.',
    },
    [SIGN_IN_STEP.EMAIL_MFA]: {
      title: 'Check your inbox',
      description: 'We are ready to send a secure verification code to your email address.',
    },
  };

  constructor() {
    // If the user already has MFA set up, skip the method selection step and go directly to the verification step
    if (this.data.gotToVerificationStep) {
      this.selectedMethod.setValue(this.data.challengeName);
      this.setTitlesAndDescriptions(2);
    } else {
      this.showPhoneOption.set(
        !!this._authService.userAttributes().phone_number &&
          this._authService.userAttributes().phone_number_verified === 'true'
      );
      this.showEmailOption.set(
        !!this._authService.userAttributes().email && this._authService.userAttributes().email_verified === 'true'
      );
    }
  }

  onStepChange(event: StepperSelectionEvent) {
    const stepIndex = event.selectedIndex;

    this.setTitlesAndDescriptions(stepIndex);
  }

  setTitlesAndDescriptions(stepIndex: number) {
    switch (stepIndex) {
      case 0:
        this.title = 'Choose your verification method';
        this.description = `Select how you want to receive your security codes.<br>
                 We support multiple methods to help keep your account secure.`;
        break;

      case 1:
        if (this.selectedMethod.value) this.title = this.STEP_2_CONTENT[this.selectedMethod.value].title;
        this.description = this.STEP_2_CONTENT[this.selectedMethod.value].description;
        break;

      case 2:
        this.mustVerify.set(true);

        this.title = 'Enter verification code';

        if (this.selectedMethod.value === SIGN_IN_STEP.SOFTWARE_TOKEN_MFA) {
          this.description = `Please enter the 6-digit code from your authenticator app to complete the Log In process.`;
        }

        if (this.selectedMethod.value === SIGN_IN_STEP.SMS_MFA) {
          this.description = `Please enter the 6-digit code sent to your phone ${this.data.deliveryDestination ?? ''} via SMS to complete the Log In process.`;
        }

        if (this.selectedMethod.value === SIGN_IN_STEP.EMAIL_MFA) {
          this.description = `Please enter the 6-digit code sent to your email address ${this.data.deliveryDestination ?? ''} to complete the Log In process.`;
        }

        break;
    }
  }

  async onSubmit(event: any) {
    this.dialogRef.close(event);
  }
}
